import { Button, Dropdown, notification, Tooltip } from 'antd';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { MasterSeriesCanvas } from 'src/model';
import { UseCardExport, useCardExport, useLanguage, useMasterSeriDrawer, useSetting } from 'src/service';
import { GatewayOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useShallow } from 'zustand/react/shallow';
import { forceRefocus } from 'src/util';
import { ResolutionPicker } from 'src/component';

export const ResolutionButton = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 var(--space-3);
    border-left: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0 var(--radius-md) var(--radius-md) 0;
    color: white;
    cursor: pointer;
    background: transparent;
    transition: all var(--transition-base);
    min-width: 50px;
    
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
    
    .anticon {
        font-size: 18px;
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    
    .resolution-overlay {
        display: none;
    }
    
    .resolution-icon {
        transform: none;
    }
`;
const StyledDownloadButton = styled(Button)`
    padding: 0;
    background: var(--accent-primary) !important;
    border: none !important;
    
    &:hover, &:focus, &:active {
        background: var(--accent-primary-hover) !important;
        border: none !important;
    }
    
    .button-label {
        height: 100%; // Alignment
        align-content: center;
        border-radius: var(--br-lg);
        line-height: 1.5;
        background: transparent;
        
        .label-content {
            padding: 4px 15px;
            border: none;
            border-radius: var(--br-lg);
            line-height: 29px; // Alignment
            background: transparent;
        }
    }
`;
export type DownloadButtonRef = {
    download: () => void,
    isPipelineRunning: () => boolean,
}
export type DownloadButton = {
    imageChangeCount: number,
    globalScale: number,
    canvasMap: MasterSeriesCanvas,
    onTainted: () => void,
    onExportSucess: () => void,
} & Pick<UseCardExport, 'isTainted'
| 'isInitializing'
| 'onDownloadError'>;
export const DownloadButton = forwardRef<DownloadButtonRef, DownloadButton>(({
    isTainted,
    isInitializing,
    imageChangeCount,
    globalScale,
    canvasMap,
    onTainted,
    onExportSucess,
    onDownloadError,
}, ref) => {
    const language = useLanguage();
    const {
        allowHotkey,
        resolution,
    } = useSetting(useShallow(({
        setting: { allowHotkey, resolution },
    }) => ({
        allowHotkey, resolution,
    })));
    const { onExport } = useMasterSeriDrawer(
        true,
        canvasMap,
        {
            imageChangeCount,
            isInitializing,
            language,
            globalScale,
        },
    );
    const {
        exportCanvasRef,
    } = canvasMap;
    const exportRef = useRef({
        currentPipeline: Promise.resolve(),
        pipelineRunning: false,
    });
    const [isDownloading, setDownloading] = useState(false);
    const onDownloadComplete = useCallback(() => {
        setDownloading(false);
    }, []);
    const onExportWithCallback = useCallback(async (param: Parameters<UseCardExport['onExport']>[0]) => {
        return onExport({
            ...param,
            onError: e => {
                if (!isTainted && String(e).includes('Tainted canvases')) {
                    onTainted();
                }
            },
            onSuccess: onExportSucess,
        });
    }, [isTainted, onExport, onExportSucess, onTainted]);
    const { onSave, isPipelineRunning } = useCardExport({
        isTainted,
        isInitializing,
        exportCanvasRef,
        exportRef,
        onExport: onExportWithCallback,
        onDownloadError,
        onDownloadComplete,
    });

    const download = (size?: [number, number]) => {
        if (isDownloading) return;
        if (isTainted) {
            notification.error({
                message: language['prompt.download.tainted.message'],
            });
            return;
        }
        setDownloading(true);
        onSave(size);
        return;
    };
    useImperativeHandle(ref, () => ({
        download,
        isPipelineRunning,
    }));

    if (isTainted) return null;
    return <div className="save-button-container">
        <div id="save-button-waiting" />
        <StyledDownloadButton className="save-button" id="save-button-ready">
            <Tooltip overlay={allowHotkey ? <>Ctrl+S / ⌘+S</> : null}>
                <div className="button-label">
                    <div className="label-content" onClick={() => download()}>
                        {isDownloading
                            ? language['button.download.ongoing.label']
                            : language['button.download.label']}
                    </div>
                </div>
            </Tooltip>
            <Dropdown
                disabled={isDownloading}
                className="save-button-dropdown"
                placement="bottomRight"
                overlay={<ResolutionPicker onChange={() => forceRefocus()} />}
            >
                <ResolutionButton className="resolution-option" onClick={e => e.stopPropagation()}>
                    <GatewayOutlined className="resolution-icon" />
                    <span className="resolution-overlay">{resolution[1]}</span>
                </ResolutionButton>
            </Dropdown>
        </StyledDownloadButton>
    </div>;
});